import { motion } from "motion/react";
import { Result } from ".";
import className from "classnames/bind";
import { forwardRef, useMemo } from "react";
import { Image, TypePill, RhLink, YoutubeThumbnail } from "@components";
import { AcfMediaFragment } from "@graphqlTypes";
import { usePostType } from "@/hooks/postType";
import ArrowRight from "@/assets/svg/arrow-tail-right-no-padding.svg";

import styles from "./RockHallResults.module.scss";
const cx = className.bind(styles);

const ResultTypePill = ({ type }: { type: Result["__typename"] }) => {
  const postType = usePostType({ node: { __typename: type } as any });
  return postType ? (
    <TypePill type={postType.key} copy={postType.label} />
  ) : null;
};

const ResultImage = ({ image, youtubeId }: { image?: AcfMediaFragment; youtubeId?: string }) => {
  if (image) {
    return <Image image={image} />;
  } if (youtubeId) {
    return <YoutubeThumbnail youtubeId={youtubeId} />;
  }
  return null;
};

const RockHallResult = forwardRef<HTMLDivElement, Result & { pressReleaseSearchImage: AcfMediaFragment }>((props, ref) => {
  const { uri, __typename, title, pressReleaseSearchImage } = props;
  const resultTitle = useMemo(() => {
    if (__typename === "RhtvVideo") {
      const { rhtvVideoContent: { videoTitle } = {} } = props;
      return videoTitle || title;
    }
    return title;
  }, []);
  const url = useMemo(() => uri, [uri, __typename]);
  
  const image = useMemo(() => {
    if (__typename === "RhtvVideo") {
      return props.rhtvVideoContent?.thumbnail || null;
    } else if (__typename === "RhtvChannel") {
      const { rhtvChannelContent: { thumbnail } } = props;
      return thumbnail;
    } else if (__typename === "PressRelease") {
      return pressReleaseSearchImage;
    }
    return props.pageData?.featuredImage;
  }, [__typename, props]);

  const youtubeId = useMemo(() => {
    if (__typename === "RhtvVideo") return props.rhtvVideoContent?.youtubeVideoId || null;
    return null;
  }, [__typename]);
  
  if (!url) return null;
  
  return (
    <div ref={ref}>
      <RhLink className={cx("rockhall-result")} href={url}>
        <div className={cx(["rockhall-result__image"])}>
          <ResultImage image={image} youtubeId={youtubeId} />
        </div>
        <div className={cx("rockhall-result__content")}>
          <ResultTypePill type={__typename} />
          <h3 className={cx(["title-lc-xs"])}>{resultTitle}</h3>
          <ArrowRight className={cx("rockhall-result__arrow")} />
        </div>
      </RhLink>
    </div>
  );
});

export default motion.create(RockHallResult);
